import { Link } from "react-router-dom";
import { Bed, Bath, Maximize, MapPin, MessageCircle, ArrowUpRight } from "lucide-react";
import { stripHtml, formatPrice } from "@/lib/utils";

const STATUS_STYLES = {
  Available: "bg-bronze text-cream",
  Sold: "bg-olive text-cream",
  Rented: "bg-ink text-cream",
  "Coming Soon": "bg-taupe text-cream",
};

export default function PropertyCard({ listing, idx = 0 }) {
  const status = listing.listingStatus || listing.status;
  const waLink = `https://wa.me/6581005654?text=${encodeURIComponent(listing.waMessage || "")}`;
  const detailPath = `/listings/${listing.slug || listing.id}`;
  return (
    <article
      data-testid={`listing-card-${listing.slug || listing.id}`}
      className="reveal group flex flex-col border border-bronze/20 bg-cream overflow-hidden"
      style={{ transitionDelay: `${idx * 80}ms` }}
    >
      <Link to={detailPath} className="relative overflow-hidden aspect-[4/3] block bg-beige">
        {listing.image ? (
          <img
            src={listing.image}
            alt={listing.title}
            // object-contain: show the WHOLE image (no cropping) whatever its
            // shape; the beige box fills any letterbox space to keep the grid tidy.
            className="h-full w-full object-contain transition-transform duration-[1200ms] ease-out group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="h-full w-full bg-beige flex items-center justify-center text-bronze/40 text-[0.65rem] tracking-[0.22em] uppercase">
            No image
          </div>
        )}
        <span
          className={`absolute top-4 left-4 px-3 py-1 text-[0.65rem] tracking-[0.22em] uppercase font-medium ${
            STATUS_STYLES[status] || "bg-bronze text-cream"
          }`}
          data-testid={`listing-status-${listing.slug || listing.id}`}
        >
          {status}
        </span>
      </Link>

      <div className="flex-1 p-7 flex flex-col">
        <div className="text-[0.7rem] tracking-[0.22em] uppercase text-bronze mb-3">
          {listing.category || listing.type}
        </div>
        <Link to={detailPath}>
          <h3 className="font-serif text-2xl sm:text-3xl text-ink leading-tight hover:text-bronze transition-colors">
            {listing.title}
          </h3>
        </Link>

        <div className="mt-3 flex items-center gap-2 text-sm text-ink_soft/70">
          <MapPin size={14} className="text-bronze" />
          <span>{listing.location}</span>
        </div>

        <p className="mt-5 text-sm font-light leading-relaxed text-ink_soft/75 line-clamp-3">
          {stripHtml(listing.description)}
        </p>

        <div className="my-6 shimmer-line" />

        <div className="grid grid-cols-3 gap-3 text-xs text-ink_soft/80">
          <div className="flex items-center gap-2">
            <Bed size={14} className="text-bronze" /> {listing.bedrooms} Bed
          </div>
          <div className="flex items-center gap-2">
            <Bath size={14} className="text-bronze" /> {listing.bathrooms} Bath
          </div>
          <div className="flex items-center gap-2">
            <Maximize size={14} className="text-bronze" /> {listing.area}
          </div>
        </div>

        <div className="mt-3 text-xs tracking-widest uppercase text-olive">
          {listing.tenure}
        </div>

        <div className="mt-auto pt-7 flex items-center justify-between gap-4">
          <span className="font-serif text-2xl text-ink">{formatPrice(listing)}</span>
          <div className="flex items-center gap-4">
            <Link
              to={detailPath}
              data-testid={`listing-detail-${listing.slug || listing.id}`}
              className="inline-flex items-center gap-1.5 text-xs tracking-[0.18em] uppercase text-ink_soft hover:text-bronze"
            >
              Details <ArrowUpRight size={14} />
            </Link>
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`listing-cta-${listing.slug || listing.id}`}
              className="inline-flex items-center gap-2 text-xs tracking-[0.18em] uppercase text-bronze hover:text-bronze_dark"
            >
              <MessageCircle size={14} /> Enquire
            </a>
          </div>
        </div>
      </div>
    </article>
  );
}
